import useTranslation from 'next-translate/useTranslation';
import Link from 'next/link';

import { WeeeIcon } from '@/components/common/Icon';
import Logo from '@/components/common/Logo';
import {
  ETHNIC_LIST_SEO,
  ETHNIC_LIST_SEO_MASGUSTO,
  ETHNIC_LIST_SEO_MASGUSTO_LANG_ES,
  ETHNIC_LIST_SEO_MASGUSTO_LANG_PT
} from '@/constants/landing';
import Constants from '@/constants/const';
import { buildUrlWithOrigin } from '@/utils/tool';
import WeeeLogo from '@/assets/images/logo/weee-logo-black.svg';
import Trans from 'next-translate/Trans';
import { isRobot } from '@/utils/common/device';
import DynamicRelatedTopicsModule from '@/components/pdp/DynamicRelatedTopicsModule';
import { isLatinoChannel } from '@/utils/common/latino-app';
import { footerControl, footerSeoControl } from '@/utils/channel-control';

const ENABLED_SITEMAPS = {
  [Constants.LANGUAGE_EN]: 'lang-en',
  [Constants.LANGUAGE_CHINESE]: 'chinese-lang',
  [Constants.LANGUAGE_CHINESE_T]: 'chinese-hant-lang',
  [Constants.LANGUAGE_JA]: 'japanese-lang',
  [Constants.LANGUAGE_VI]: 'vietnamese-lang',
  [Constants.LANGUAGE_KO]: 'korean-lang',
  [Constants.LANGUAGE_PT]: 'portuguese-lang',
  [Constants.LANGUAGE_ES]: 'spanish-lang'
};

const ENABLED_SITEMAPS_MASGUSTO = {
  [Constants.LANGUAGE_EN]: 'lang-en',
  [Constants.LANGUAGE_ES]: 'spanish-lang',
  [Constants.LANGUAGE_PT]: 'portuguese-lang'
};

const LOCATIONS_CITIES = [
  { newKey: 'california/city-san-francisco', key: 'San Francisco-California', labelKey: 'footer_locations_sa' },
  { newKey: 'california/city-los-angeles', key: 'Los Angeles-California', labelKey: 'footer_locations_la' },
  { newKey: 'washington/city-seattle', key: 'Seattle-Washington', labelKey: 'footer_locations_se' },
  { newKey: 'oregon/city-portland', key: 'Portland-Oregon', labelKey: 'footer_locations_pd' },
  { newKey: 'california/city-san-diego', key: 'San Diego-California', labelKey: 'footer_locations_sd' },
  { newKey: 'new-york/city-new-york', key: 'New York-New York', labelKey: 'footer_locations_ny' },
  { newKey: 'florida/city-miami', key: 'Miami-Florida', labelKey: 'footer_locations_mia' },
  { newKey: 'florida/city-tampa', key: 'Tampa-Florida', labelKey: 'footer_locations_tp' },
  { newKey: 'florida/city-orlando', key: 'Orlando-Florida', labelKey: 'footer_locations_orl' },
  { newKey: 'texas/city-houston', key: 'Houston-Texas', labelKey: 'footer_locations_hou' },
  {
    newKey: 'district-of-columbia/city-washington',
    key: 'Washington-District Of Columbia',
    labelKey: 'footer_locations_was'
  },
  { newKey: 'new-jersey/city-jersey-city', key: 'Jersey City-New Jersey', labelKey: 'footer_locations_nj' },
  { newKey: 'illinois/city-chicago', key: 'Chicago-Illinois', labelKey: 'footer_locations_chi' },
  { newKey: 'massachusetts/city-boston', key: 'Boston-Massachusetts', labelKey: 'footer_locations_bos' },
  { newKey: 'georgia/city-atlanta', key: 'Atlanta-Georgia', labelKey: 'footer_locations_atl' },
  { newKey: 'arizona/city-phoenix', key: 'Phoenix-Arizona', labelKey: 'footer_locations_phx' },
  { newKey: 'texas/city-dallas', key: 'Dallas-Texas', labelKey: 'footer_locations_dal' },
  { newKey: 'nevada/city-las-vegas', key: 'Las Vegas-Nevada', labelKey: 'footer_locations_lv' }
];

const LOCATIONS_CITIES_MASGUSTO = [
  { newKey: 'california/city-san-francisco', key: 'San Francisco-California', labelKey: 'footer_locations_sa' },
  { newKey: 'california/city-los-angeles', key: 'Los Angeles-California', labelKey: 'footer_locations_la' },
  { newKey: 'washington/city-seattle', key: 'Seattle-Washington', labelKey: 'footer_locations_se' },
  { newKey: 'oregon/city-portland', key: 'Portland-Oregon', labelKey: 'footer_locations_pd' },
  { newKey: 'california/city-san-diego', key: 'San Diego-California', labelKey: 'footer_locations_sd' },
  { newKey: 'new-york/city-new-york', key: 'New York-New York', labelKey: 'footer_locations_ny' },
  { newKey: 'florida/city-miami', key: 'Miami-Florida', labelKey: 'footer_locations_mia' },
  { newKey: 'florida/city-tampa', key: 'Tampa-Florida', labelKey: 'footer_locations_tp' },
  { newKey: 'florida/city-orlando', key: 'Orlando-Florida', labelKey: 'footer_locations_orl' },
  { newKey: 'texas/city-houston', key: 'Houston-Texas', labelKey: 'footer_locations_hou' },
  {
    newKey: 'district-of-columbia/city-washington',
    key: 'Washington-District Of Columbia',
    labelKey: 'footer_locations_was'
  },
  { newKey: 'new-jersey/city-jersey-city', key: 'Jersey City-New Jersey', labelKey: 'footer_locations_nj' },
  { newKey: 'illinois/city-chicago', key: 'Chicago-Illinois', labelKey: 'footer_locations_chi' },
  { newKey: 'massachusetts/city-boston', key: 'Boston-Massachusetts', labelKey: 'footer_locations_bos' },
  { newKey: 'georgia/city-atlanta', key: 'Atlanta-Georgia', labelKey: 'footer_locations_atl' },
  { newKey: 'arizona/city-phoenix', key: 'Phoenix-Arizona', labelKey: 'footer_locations_phx' },
  { newKey: 'texas/city-dallas', key: 'Dallas-Texas', labelKey: 'footer_locations_dal' },
  { newKey: 'nevada/city-las-vegas', key: 'Las Vegas-Nevada', labelKey: 'footer_locations_lv' }
];

const VENDOR_ONBOARDING_URL_PER_LANG = {
  [Constants.LANGUAGE_EN]: '/company/vendor/onboarding',
  [Constants.LANGUAGE_CHINESE]: '/company/vendor/onboarding-zh',
  [Constants.LANGUAGE_CHINESE_T]: '/company/vendor/onboarding-zht'
};
const DEFAULT_VENDOR_ONBOARDING_LANG = Constants.LANGUAGE_EN;

const downloadUrl = 'https://www.sayweee.com/mgp/download-app';

export default function Footer(props) {
  const { t, lang } = useTranslation('common');
  const isLatino = isLatinoChannel();
  const aboutControl = footerControl();
  const seoControl = footerSeoControl();

  const enabledSitemapList = isLatino ? ENABLED_SITEMAPS_MASGUSTO : ENABLED_SITEMAPS;
  const ethnicStorePrefix = isLatino ? 'latino-supermarket-in-usa' : 'asian-supermarket-in-usa';
  const cityPageHostname = isLatino ? 'https://www.masgusto.com' : 'https://www.sayweee.com';
  const cityPagePrefix = isLatino ? 'latino-supermarket-in-usa' : 'asian-supermarket-in-usa';
  const ENABLED_NEW_ETHNIC_LIST = true;

  let crossDomainEthnicList;
  if (!isLatino && lang === Constants.LANGUAGE_EN) {
    crossDomainEthnicList = ETHNIC_LIST_SEO_MASGUSTO;
  }
  const incLinks = [
    { href: buildUrlWithOrigin(`/${lang}/about/terms-of-service`), text: t('footer_terms') },
    { href: buildUrlWithOrigin(`/${lang}/about/privacy-policy`), text: t('footer_privacy') },
    {
      href: buildUrlWithOrigin(`/${lang}/about/accessibility-statement`),
      text: t('footer_accessibility')
    },
    {
      href: buildUrlWithOrigin(`/${lang}/about/social-media-influencer-policy`),
      text: t('home_social_media_influencer_policy')
    }
  ];

  const vendorOnboardingLink = (() => {
    let link;
    if (VENDOR_ONBOARDING_URL_PER_LANG[lang]) {
      link = VENDOR_ONBOARDING_URL_PER_LANG[lang];
    } else {
      link = VENDOR_ONBOARDING_URL_PER_LANG[DEFAULT_VENDOR_ONBOARDING_LANG];
    }

    return buildUrlWithOrigin(`/${lang}${link}`);
  })();

  const doesLangAllowEthnicStorePages =
    lang === Constants.LANGUAGE_EN || (isLatino && lang === Constants.LANGUAGE_ES) || lang === Constants.LANGUAGE_PT;

  let ethnicList;
  if (!ENABLED_NEW_ETHNIC_LIST) {
    ethnicList = isLatino ? ETHNIC_LIST_SEO_MASGUSTO : ETHNIC_LIST_SEO;
  } else {
    if (isLatino) {
      if (lang === Constants.LANGUAGE_ES) {
        ethnicList = ETHNIC_LIST_SEO_MASGUSTO_LANG_ES;
      } else if (lang === Constants.LANGUAGE_PT) {
        ethnicList = ETHNIC_LIST_SEO_MASGUSTO_LANG_PT;
      } else {
        ethnicList = ETHNIC_LIST_SEO_MASGUSTO;
      }
    } else {
      ethnicList = ETHNIC_LIST_SEO;
    }
  }

  const __renderSitemap = (lang: string) => {
    try {
      if (!enabledSitemapList[lang]) return null;

      return (
        <a
          key={'sitemap'}
          aria-label="weee footer sitemap"
          href={buildUrlWithOrigin(`/${lang}/grocery-near-me/${enabledSitemapList[lang]}/sitemap`)}
          className=" text-surface-200-fg-default">
          Sitemap
        </a>
      );
    } catch (e) {}
  };

  const CITIES_OBJ = isLatino ? LOCATIONS_CITIES_MASGUSTO : LOCATIONS_CITIES;

  return (
    <div className="bg-surface-200-bg enki-body-sm text-surface-200-fg-default py-10">
      <div className="flex flex-col gap-12">
        <div className="flex flex-col items-center justify-center">
          <Logo />
          <p className="mt-2 text-surface-200-fg-minor">{t('location_banner_slogn')}</p>
        </div>

        {!!props.relatedTopicsData?.length && (
          <div>
            <DynamicRelatedTopicsModule
              className="my-0"
              contentWrapCls="mt-0"
              data={props.relatedTopicsData}
              title={t('trending_topics')}
            />
          </div>
        )}

        {
          <div className="text-center flex flex-col gap-3 ">
            <div className="font-semibold">{t('footer_locations')}</div>
            <div className="flex flex-col justify-between gap-3">
              {CITIES_OBJ.map((item, index) => {
                const newUrl = `https://www.${
                  isLatino ? 'masgusto' : 'sayweee'
                }.com/${lang}/grocery-near-me/${cityPagePrefix}/${item.newKey}`;
                return (
                  <a
                    className=" text-surface-200-fg-default"
                    key={index}
                    aria-label={`location city ${item.key}`}
                    href={newUrl}>
                    {t(item.labelKey)}
                  </a>
                );
              })}
              <a
                aria-label="weee footer location"
                className=" text-surface-200-fg-default"
                href={buildUrlWithOrigin(`/${lang}/grocery-delivery`)}>
                {t('footer_locations')}
              </a>
              {!doesLangAllowEthnicStorePages && __renderSitemap(lang)}
            </div>
          </div>
        }

        {aboutControl && (
          <div className="text-center flex flex-col gap-3">
            <div className="font-semibold">{t('footer_about')}</div>
            <a
              aria-label={`weee footer company`}
              className=" text-surface-200-fg-default"
              href="https://www.sayweee.com/company/home">
              {t('footer_company')}
            </a>
            <a
              aria-label={`weee footer careers`}
              className=" text-surface-200-fg-default"
              href="https://www.sayweee.com/company/careers">
              {t('footer_about_careers')}
            </a>
            <a
              aria-label={`weee footer about media`}
              className=" text-surface-200-fg-default"
              href="https://www.sayweee.com/company/press-and-media">
              {t('footer_about_media')}
            </a>
            <a
              aria-label={`weee footer about student`}
              className=" text-surface-200-fg-default"
              href={`https://www.sayweee.com/company/students-${lang}`}>
              {t('footer_about_student')}
            </a>
            {isRobot() && (
              <>
                <a
                  aria-label={`weee footer coupons`}
                  className=" text-surface-200-fg-default"
                  href="https://www.sayweee.com/company/coupons">
                  {'Coupons'}
                </a>
                <a
                  aria-label={`weee footer lightning deals`}
                  className=" text-surface-200-fg-default"
                  href="https://www.sayweee.com/company/lightning-deals">
                  {'Lightning Deals'}
                </a>
              </>
            )}
          </div>
        )}

        {seoControl && doesLangAllowEthnicStorePages && (
          <div className="text-center flex flex-col gap-3  ">
            <div className="font-semibold">{t('footer_ethnic')}</div>
            {ethnicList.map(item => {
              return (
                <Link
                  prefetch={false}
                  key={`ethnic_${item}`}
                  href={`${cityPageHostname}/${lang}/grocery-near-me/${ethnicStorePrefix}/${item}-store`}>
                  <a aria-label={`weee footer ethnic ${item}`} className=" text-surface-200-fg-default" target="_blank">
                    {t(`footer_ethnic_${item}`)}
                  </a>
                </Link>
              );
            })}
            {!isLatino && (
              <>
                {crossDomainEthnicList?.map(item => {
                  const _href = `https://www.masgusto.com/${lang}/grocery-near-me/latino-supermarket-in-usa/${item}-store`;
                  return (
                    <a
                      key={`ethnic_cross_${item}`}
                      aria-label={`footer ethnic ${item}`}
                      href={_href}
                      target="_blank"
                      className=" text-surface-200-fg-default"
                      rel="noreferrer">
                      {t(`footer_ethnic_${item}`)}
                    </a>
                  );
                })}
              </>
            )}
            {__renderSitemap(lang)}
          </div>
        )}

        <div className="text-center flex flex-col gap-3 ">
          <div className="font-semibold">{t('footer_help')}</div>
          {aboutControl && (
            <a
              aria-label={`weee footer sell on weee`}
              className=" text-surface-200-fg-default flex items-center justify-center gap-1"
              href={vendorOnboardingLink}>
              <Trans
                i18nKey="common:footer_sell_on_weee"
                components={{
                  img: <img className="h-[12px]" src={WeeeLogo} aria-label="weee logo" alt="Weee! logo" />
                }}
              />
            </a>
          )}
          <a
            aria-label={`weee footer help contact`}
            className=" text-surface-200-fg-default"
            href={`/${lang}/account/help`}>
            {t('footer_help_contact')}
          </a>
          <a aria-label={`weee footer help center`} className="text-surface-200-fg-default" href={`/${lang}/help`}>
            {t('footer_help_center')}
          </a>
        </div>

        <div className="flex flex-col gap-3 items-center justify-center">
          <div className="font-semibold">{t('footer_download')}</div>
          <a aria-label="weee download ios app" href={downloadUrl}>
            <img width="150" src={require('@/assets/images/app-qrcode/ios.png')} alt="ios image" />
          </a>
          <a aria-label="weee download android app" href={downloadUrl}>
            <img width="150" src={require('@/assets/images/app-qrcode/android.png')} alt="android image" />
          </a>
        </div>

        {aboutControl && (
          <div className="flex items-center justify-center">
            <a
              aria-label="Weee on facebook"
              className="mx-5 text-surface-200-fg-default"
              href="https://www.facebook.com/sayweee/?ref=aymt_homepage_panel"
              rel="noopener noreferrer">
              <WeeeIcon type="iconfacebook-f" />
            </a>
            <a
              aria-label="Weee on instagram"
              className="mx-5 text-surface-200-fg-default"
              href="https://www.instagram.com/sayweee_official"
              rel="noopener noreferrer">
              <WeeeIcon type="iconinstagram" />
            </a>
            <a
              aria-label="Weee on linkedin"
              className="mx-5 text-surface-200-fg-default"
              href="https://www.linkedin.com/company/weee-/"
              rel="noopener noreferrer">
              <WeeeIcon type="iconLinkedin-in" />
            </a>
            <a
              aria-label="Weee on youtube"
              className="mx-5 text-surface-200-fg-default"
              href="https://www.youtube.com/channel/UCmtycD943w_ZI2illCA9tvA"
              rel="noopener noreferrer">
              <WeeeIcon type="iconyoutube" />
            </a>
            <a
              aria-label="Weee on twitter"
              className="mx-5 text-surface-200-fg-default"
              href="https://twitter.com/sayweeeofficial"
              rel="noopener noreferrer">
              <WeeeIcon type="icontwitter" />
            </a>
          </div>
        )}

        <div className="flex flex-col items-center justify-center enki-body-base text-surface-200-fg-default text-center">
          <p>© 2015 - {new Date().getFullYear()} Weee!, Inc.</p>
          <div className="mt-3">
            {incLinks.map((link, index) => (
              <Link prefetch={false} href={link.href} key={index}>
                <a
                  aria-label={`weee text- ${link.text}`}
                  className="enki-body-base text-surface-200-fg-default px-2 inline-block border-r"
                  style={index === incLinks.length - 1 ? { borderRight: 'none' } : { borderRightStyle: 'solid' }}>
                  {link.text}
                </a>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
